import { HelpCircle } from '@untitledui/icons';
import { Label as AriaLabel } from 'react-aria-components';

import { Tooltip, TooltipTrigger } from '@/components/base/tooltip/tooltip';
import { cx } from '@/lib/utils/cx';

import type { LabelProps as AriaLabelProps } from 'react-aria-components';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';

interface LabelProps extends AriaLabelProps {
  readonly children: ReactNode;
  readonly isInvalid?: boolean;
  readonly isRequired?: boolean;
  readonly tooltip?: string;
  readonly tooltipDescription?: string;
}

export const Label = ({
  isInvalid,
  isRequired,
  tooltip,
  tooltipDescription,
  className,
  children,
  htmlFor,
  ...props
}: LabelProps) => {
  const resolvedClassName = cx(
    'flex cursor-default items-center gap-0.5 text-sm font-medium text-secondary',
    className,
  );
  const renderContent = (
    includeImplicitRequiredIndicator: boolean,
    useCssRequiredIndicator = false,
  ) => (
    <>
      {children}

      {includeImplicitRequiredIndicator || isRequired ? (
        <span
          aria-hidden="true"
          data-required-indicator="true"
          className={cx(
            'hidden text-brand-tertiary',
            useCssRequiredIndicator && "after:content-['*']",
            isRequired && 'block',
            isRequired === undefined && 'group-required:block',

            isInvalid && 'text-error-primary',
            isInvalid === undefined && 'group-invalid:text-error-primary',
          )}
        >
          {useCssRequiredIndicator ? null : '*'}
        </span>
      ) : null}

      {tooltip ? (
        <Tooltip description={tooltipDescription} placement="top" title={tooltip}>
          <TooltipTrigger
            // `TooltipTrigger` inherits the disabled state from the parent form field
            // but we don't that. We want the tooltip be enabled even if the parent
            // field is disabled.
            className="cursor-pointer text-fg-quaternary transition duration-200 hover:text-fg-quaternary_hover focus:text-fg-quaternary_hover"
            isDisabled={false}
          >
            <HelpCircle className="size-4" />
          </TooltipTrigger>
        </Tooltip>
      ) : null}
    </>
  );

  if (htmlFor) {
    return (
      <label
        {...(props as ComponentPropsWithoutRef<'label'>)}
        className={resolvedClassName}
        data-label="true"
        htmlFor={htmlFor}
      >
        {renderContent(true, true)}
      </label>
    );
  }

  return (
    <AriaLabel className={resolvedClassName} data-label="true" {...props}>
      {renderContent(true)}
    </AriaLabel>
  );
};

Label.displayName = 'Label';
